/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CannedCollectionModule, type CannedCollectionModuleCard } from '@coursera-web/cds-labs';

import type { MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { PromoTemplateCannedCollectionFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import Section from 'bundles/front-page/components/Section';
import getPromoUnitProps from 'bundles/front-page/redesign-modules/promo-units/getPromoUnitProps';
import normalizePromoUnitsModuleContent from 'bundles/front-page/utils/normalizePromoUnitsModuleContent';

type Props = {
  content?: PromoTemplateCannedCollectionFragment;
};

const styles = {
  promoUnitsModule: css`
    display: flex;
    flex-direction: row;
    justify-content: center;

    /* Outermost container for CannedCollectionModule */
    > div {
      display: flex !important;
      padding: 0 !important;
      margin: 0 !important;
    }
  `,
};

const PromoUnitsModuleRenderer = ({ content }: Props) => {
  const track = useTracker();

  let courseraPlusProps: Omit<CannedCollectionModuleCard, 'onClick'> | undefined;
  let courseraForBusinessProps: Omit<CannedCollectionModuleCard, 'onClick'> | undefined;

  if (content) {
    const normalizedContent = normalizePromoUnitsModuleContent(content);
    courseraPlusProps = normalizedContent[0];
    courseraForBusinessProps = normalizedContent[1];
  }

  if (!courseraPlusProps || !courseraForBusinessProps) {
    const defaultPromoUnits = getPromoUnitProps();
    courseraPlusProps = defaultPromoUnits.courseraPlusProps;
    courseraForBusinessProps = defaultPromoUnits.courseraForBusinessProps;
  }

  const handleCourseraPlusClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: {
        moduleType: 'promo_unit' as MerchandisingModuleModuleType,
        heading: courseraPlusProps.headline,
      },
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkName: courseraPlusProps.ctaText,
        linkURL: courseraPlusProps.target,
      },
    });
  };

  const handleCourseraForBusinessClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: {
        moduleType: 'promo_unit' as MerchandisingModuleModuleType,
        heading: courseraForBusinessProps.headline,
      },
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkName: courseraForBusinessProps.ctaText,
        linkURL: courseraForBusinessProps.target,
      },
    });
  };

  const courseraPlusCard = { ...courseraPlusProps, onClick: handleCourseraPlusClick };
  const courseraForBusinessCard = { ...courseraForBusinessProps, onClick: handleCourseraForBusinessClick };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: {
        moduleType: 'promo_unit',
      },
    },
    [],
    { fullyVisible: true }
  );

  return (
    <Section css={styles.promoUnitsModule} dataTestId="promo-units" stickToPrevious={!!content?.stickToPrevious}>
      <div ref={visibilityTrackingRef}>
        <CannedCollectionModule
          cards={[courseraPlusCard, courseraForBusinessCard]}
          size="large"
          color="blue"
          showImageInFull={true}
          showAllEntries={true}
          variantConfig={{
            sm: 'grid',
          }}
        />
      </div>
    </Section>
  );
};

export default PromoUnitsModuleRenderer;
